/** Normalized shipping address fields stored on orders. */
export interface ShippingAddressFields {
  name?: string;
  phone?: string;
  address1?: string;
  address2?: string;
  address3?: string;
  city?: string;
  postcode?: string;
  state?: string;
  country?: string;
}

export interface PickListSkuItem {
  sku: string;
  name: string;
  quantity: number;
}

export interface PickGroupOrderItem {
  id: string;
  name: string;
  quantity: number;
  sku?: string;
}

export interface PickGroupOrder {
  id: string;
  orderNo: string;
  items: PickGroupOrderItem[];
  pickupScheduledAt?: string | null;
}

export interface PickGroup {
  groupKey: string;
  userId: string;
  displayName: string;
  email: string;
  shippingAddress: ShippingAddressFields;
  addressSummary: string;
  fulfilmentMethod?: "DELIVERY" | "PICKUP";
  pickupScheduledAt?: string | null;
  orders: PickGroupOrder[];
  pickItems: PickListSkuItem[];
}

function norm(value: unknown): string {
  return String(value ?? "")
    .trim()
    .toLowerCase();
}

/** Stable key for grouping orders by ship-to address. */
export function shippingAddressKey(addr: ShippingAddressFields | Record<string, unknown> | null | undefined): string {
  if (!addr || typeof addr !== "object") return "unknown";
  const a = addr as ShippingAddressFields;
  return [
    norm(a.address1),
    norm(a.address2),
    norm(a.address3),
    norm(a.postcode),
    norm(a.city),
    norm(a.state),
    norm(a.country),
  ].join("|");
}

export function pickGroupKey(userId: string, addr: ShippingAddressFields | Record<string, unknown> | null | undefined): string {
  return `${userId}::${shippingAddressKey(addr)}`;
}

export function formatShippingAddressLines(
  addr: ShippingAddressFields | Record<string, unknown> | null | undefined,
): string[] {
  if (!addr || typeof addr !== "object") return [];
  const a = addr as ShippingAddressFields;
  return [
    a.name,
    a.phone,
    a.address1,
    a.address2,
    a.address3,
    [a.city, a.postcode].filter(Boolean).join(", "),
    [a.state, a.country].filter(Boolean).join(", "),
  ].filter((line): line is string => Boolean(line && String(line).trim()));
}

/** One-line label for grouping headers. */
export function formatShippingAddressSummary(
  addr: ShippingAddressFields | Record<string, unknown> | null | undefined,
): string {
  if (!addr || typeof addr !== "object") return "Unknown address";
  const a = addr as ShippingAddressFields;
  const parts = [a.address1, a.city, a.postcode, a.state, a.country].filter(Boolean);
  return parts.length > 0 ? parts.join(", ") : "Unknown address";
}

export function parseShippingAddress(
  raw: unknown,
): ShippingAddressFields {
  if (!raw || typeof raw !== "object") return {};
  const a = raw as Record<string, unknown>;
  return {
    name: a.name != null ? String(a.name) : undefined,
    phone: a.phone != null ? String(a.phone) : undefined,
    address1: a.address1 != null ? String(a.address1) : undefined,
    address2: a.address2 != null ? String(a.address2) : undefined,
    address3: a.address3 != null ? String(a.address3) : undefined,
    city: a.city != null ? String(a.city) : undefined,
    postcode: a.postcode != null ? String(a.postcode) : undefined,
    state: a.state != null ? String(a.state) : undefined,
    country: a.country != null ? String(a.country) : undefined,
  };
}
